import React from 'react';
import { Button, Input, Select, Table, Tag, Upload, Space, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Text } = Typography;

const AUDIENCE_OPTIONS = [
  { value: 'public', label: '全部可见（含访客）' },
  { value: 'registered', label: '仅登录用户' },
  { value: 'admin', label: '仅管理员' },
  { value: 'groups', label: '指定权限组' }
];

function ToolAccessCell({ record, groups, onChange }) {
  const audience = record.audience || (record.group_ids?.length ? 'groups' : 'public');
  const groupOptions = (groups || []).map((g) => ({ label: g.name, value: g.id }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
      <Select
        value={audience}
        options={AUDIENCE_OPTIONS}
        onChange={(value) => onChange(record, value, record.group_ids || [])}
        style={{ width: '100%' }}
      />
      {audience === 'groups' ? (
        <Select
          mode="multiple"
          allowClear
          placeholder="选择可见权限组"
          value={record.group_ids || []}
          options={groupOptions}
          optionFilterProp="label"
          onChange={(vals) => onChange(record, 'groups', (vals || []).map(Number))}
          style={{ width: '100%' }}
        />
      ) : null}
    </div>
  );
}

/** 构建工具管理标签页（每条工具可单独设置谁能看、谁能下载）。 */
export function buildToolsTab(context) {
  const {
    shouldShowPage,
    toolName,
    setToolName,
    toolUrl,
    setToolUrl,
    toolDescription,
    setToolDescription,
    toolAudience,
    setToolAudience,
    toolGroupIds,
    setToolGroupIds,
    editId,
    editBuiltin,
    editMobileRelease,
    resetToolForm,
    handleSaveTool,
    handleUpdateToolAccess,
    handleUploadToolFile,
    toolUploading,
    toolMsg,
    toolVersionName,
    setToolVersionName,
    toolVersionCode,
    setToolVersionCode,
    tools,
    groups,
    handleEditTool,
    handleDeleteTool
  } = context;

  const lockRemoteUrl = !!editBuiltin && !editMobileRelease;

  if (!shouldShowPage('工具管理')) return null;

  const groupOptions = (groups || []).map((g) => ({ label: g.name, value: g.id }));

  return {
    key: 'tools',
    label: '工具管理',
    children: (
      <div>
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13, lineHeight: 1.6 }}>
          在表格的<strong>权限设置</strong>列里，每个工具可单独选择谁能看到和下载：
          「全部可见」含未登录访客；「仅登录用户」给所有注册账号；「仅管理员」只给管理员组；
          「指定权限组」可勾选例如队长、队员等。改完立即保存，不必再点上方更新。
          谷歌地球实时图层不是上传文件，请把下载链接填成 <code>/api/kml-export/google-earth</code>，不要点「上传文件」。
          单兵日常更新：先点编辑「单兵安卓/鸿蒙更新包」，再上传 APK 或 HAP。Locus 改装包：编辑「Locus安卓更新包」后上传 APK（versionCode 须大于真机已装，基线约 1221）。版本号从安装包内读取；手工改成与包内不一致时保存会报错。下一版请先把 Gradle / app.json5 的 versionCode 提高后再编译上传（队员已装 26 时，新包须 ≥27）。
        </div>
        <Space wrap style={{ marginBottom: 12 }} size="middle">
          <Input
            placeholder="工具名称"
            value={toolName}
            onChange={(e) => setToolName(e.target.value)}
            style={{ width: 220 }}
          />
          <Input
            placeholder="下载链接：外链或接口，例如 /api/kml-export/google-earth"
            value={toolUrl}
            onChange={(e) => setToolUrl(e.target.value)}
            style={{ width: 320 }}
            disabled={lockRemoteUrl}
          />
          <Select
            value={toolAudience || 'public'}
            options={AUDIENCE_OPTIONS}
            onChange={setToolAudience}
            style={{ minWidth: 180 }}
          />
          {(toolAudience || 'public') === 'groups' ? (
            <Select
              mode="multiple"
              allowClear
              placeholder="选择可见权限组"
              value={toolGroupIds}
              onChange={(vals) => setToolGroupIds((vals || []).map(Number))}
              options={groupOptions}
              style={{ minWidth: 240 }}
              optionFilterProp="label"
            />
          ) : null}
          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              handleUploadToolFile?.(file);
              return false;
            }}
            disabled={!!toolUploading || lockRemoteUrl}
          >
            <Button icon={<UploadOutlined />} loading={!!toolUploading} disabled={lockRemoteUrl}>
              上传文件
            </Button>
          </Upload>
          <Button type="primary" onClick={handleSaveTool} disabled={!!toolUploading}>
            {editId ? '更新' : '添加'}
          </Button>
          {editId && (
            <Button onClick={() => resetToolForm?.()}>
              取消
            </Button>
          )}
        </Space>
        <Space wrap style={{ marginBottom: 12 }} size="middle" align="center">
          <span style={{ color: editMobileRelease ? '#1677ff' : '#666', fontSize: 13, whiteSpace: 'nowrap' }}>
            {editMobileRelease ? '更新包版本（与安装包内一致；包内是多少这里就显示多少）' : '版本（选填，单兵/Locus 更新包必填）'}
          </span>
          <Input
            addonBefore="版本名"
            placeholder="如 0.4.9"
            value={toolVersionName || ''}
            onChange={(e) => setToolVersionName?.(e.target.value)}
            style={{ width: 220 }}
          />
          <Input
            addonBefore="构建号"
            placeholder="如 27"
            value={toolVersionCode || ''}
            onChange={(e) => setToolVersionCode?.(e.target.value.replace(/[^\d]/g, ''))}
            style={{ width: 200 }}
          />
        </Space>
        <Input.TextArea
          placeholder="说明（选填，会显示在工具下载页，例如使用方法）"
          value={toolDescription}
          onChange={(e) => setToolDescription(e.target.value)}
          rows={2}
          style={{ marginBottom: 12, maxWidth: 720 }}
        />
        {toolMsg && (
          <div style={{ color: toolMsg.includes('成功') ? 'green' : 'red', marginBottom: 16 }}>
            {toolMsg}
          </div>
        )}
        {toolUrl ? (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">当前链接：</Text>
            <Text code copyable style={{ wordBreak: 'break-all' }}>{toolUrl}</Text>
          </div>
        ) : null}
        <Table
          rowKey="id"
          dataSource={tools}
          columns={[
            { title: 'ID', dataIndex: 'id', key: 'id', width: 64 },
            {
              title: '名称',
              dataIndex: 'name',
              key: 'name',
              width: 200,
              render: (name, record) => (
                <span>
                  {name}
                  {record.is_builtin ? <Tag color="blue" style={{ marginLeft: 6 }}>内置</Tag> : null}
                </span>
              )
            },
            {
              title: '权限设置',
              key: 'access',
              width: 260,
              render: (_, record) => (
                <ToolAccessCell
                  record={record}
                  groups={groups}
                  onChange={handleUpdateToolAccess}
                />
              )
            },
            {
              title: '版本',
              key: 'version',
              width: 120,
              render: (_, record) => {
                const name = record.version_name || '';
                const code = record.version_code;
                if (!name && !code) return '—';
                return code ? `${name || '—'} (${code})` : name;
              }
            },
            {
              title: '链接',
              dataIndex: 'url',
              key: 'url',
              ellipsis: true,
              render: (url) => (
                <a href={url?.startsWith('http') ? url : undefined} target="_blank" rel="noreferrer">
                  {url}
                </a>
              )
            },
            {
              title: '创建时间',
              dataIndex: 'created_at',
              key: 'created_at',
              width: 168,
              render: (text) => {
                if (!text) return '-';
                const d = new Date(text);
                return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
              }
            },
            {
              title: '操作',
              key: 'action',
              width: 130,
              render: (_, record) => (
                <span>
                  <Button type="link" onClick={() => handleEditTool(record)}>编辑</Button>
                  {!record.is_builtin && (
                    <Button type="link" danger onClick={() => handleDeleteTool(record.id)}>删除</Button>
                  )}
                </span>
              )
            }
          ]}
        />
      </div>
    )
  };
}
